import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BulkImport = ({ primary, onClose }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const downloadTemplate = () => {
    const rows = [
      'serial_number,gpon_serial,area',
      'HWTC5E23449C,HWTC5E23449C,Payyanur',
      '123454CAE1CB65174,HWTC1CB65174,Taliparamba'
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'fibraacs_bulk_import_template.csv';
    a.click();
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g,''));
    const data = [];
    const errs = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g,''));
      const row = {};
      headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
      if (!row.serial_number) { errs.push('Row ' + i + ': Serial number required'); continue; }
      data.push({ ...row, _rowNum: i });
    }
    setCsvData(data);
    setErrors(errs);
    if (data.length > 0) setStep(2);
  };

  const handleFile = (file) => {
    if (!file || !file.name.endsWith('.csv')) { setErrors(['Please upload a CSV file']); return; }
    const reader = new FileReader();
    reader.onload = e => parseCSV(e.target.result);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post('/api/devices/bulk-import', { devices: csvData });
      setResults(res.data);
      setStep(3);
    } catch (e) {
      setErrors([e.response?.data?.message || 'Import failed']);
    } finally { setImporting(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '680px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>📥 Bulk ONT Import</h2>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Import ONT serials — customer details auto-filled from TR-069</p>
          </div>
          <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
          {[{ num: 1, label: 'Upload CSV' }, { num: 2, label: 'Preview' }, { num: 3, label: 'Done' }].map((s, i) => (
            <React.Fragment key={s.num}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: step >= s.num ? primary : '#f1f5f9', color: step >= s.num ? 'white' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
                  {step > s.num ? '✓' : s.num}
                </div>
                <span style={{ fontSize: '13px', fontWeight: step === s.num ? '700' : '400', color: step === s.num ? '#0f172a' : '#94a3b8', whiteSpace: 'nowrap' }}>{s.label}</span>
              </div>
              {i < 2 && <div style={{ flex: 1, height: '2px', background: step > s.num ? primary : '#f1f5f9', borderRadius: '1px', alignSelf: 'center', margin: '0 4px' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div>
            {/* How it works */}
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: '#0369a1', marginBottom: '10px' }}>🤖 How Auto-Registration Works</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', color: '#0369a1' }}>
                {[
                  { icon: '📋', text: 'You provide: Serial number + area' },
                  { icon: '🤖', text: 'Auto-filled: PPPoE username → Customer ID' },
                  { icon: '📡', text: 'Auto-filled: Vendor & Model from TR-069' },
                  { icon: '👤', text: 'Auto-filled: LCO & Sub-Dist from your login' },
                  { icon: '🌐', text: 'Auto-filled: WAN IP & connection details' },
                  { icon: '✅', text: 'Later: Add customer name & billing details' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{item.icon}</span><span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={downloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '16px' }}>
              📄 Download CSV Template (3 columns only)
            </button>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById('csv-upload').click()}
              style={{ border: `2px dashed ${dragOver ? primary : '#e2e8f0'}`, borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer', background: dragOver ? `${primary}08` : '#f8fafc', transition: 'all 0.2s' }}>
              <input id="csv-upload" type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>📂</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Drop CSV file here or click to browse</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Only 3 columns needed: serial_number, gpon_serial, area</div>
            </div>

            {/* CSV format */}
            <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>📋 Minimal CSV Format:</div>
              <div style={{ fontFamily: 'monospace', fontSize: '12px', background: 'white', padding: '10px', borderRadius: '6px', color: '#374151' }}>
                serial_number,gpon_serial,area<br/>
                HWTC5E23449C,HWTC5E23449C,Payyanur<br/>
                123454CAE1CB65174,HWTC1CB65174,Taliparamba
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                💡 Export serial numbers directly from OLT using: <strong>display ont info 0 all</strong> (Huawei)
              </div>
            </div>

            {errors.length > 0 && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                {errors.map((e, i) => <div key={i} style={{ fontSize: '13px', color: '#dc2626' }}>⚠️ {e}</div>)}
              </div>
            )}
          </div>
        )}

        {/* Step 2 - Preview */}
        {step === 2 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>✅ {csvData.length} ONTs ready</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Will be assigned to: <strong>{user?.name}</strong> ({user?.role?.replace(/_/g,' ')})
                </div>
              </div>
              <button onClick={() => setStep(1)} style={{ padding: '6px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#475569', fontWeight: '600' }}>← Change File</button>
            </div>

            {/* What will be auto-filled */}
            <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px', marginBottom: '14px', fontSize: '12px', color: '#16a34a' }}>
              🤖 Auto-filled after ONT connects to ACS: PPPoE Username, Vendor, Model, WAN IP, Signal Strength
            </div>

            <div style={{ overflowX: 'auto', maxHeight: '300px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                  <tr>
                    {['#', 'Serial Number', 'GPON Serial', 'Area', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.slice(0, 100).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#94a3b8' }}>{i+1}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px', color: '#475569' }}>{row.serial_number}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px', color: '#475569' }}>{row.gpon_serial || '—'}</td>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>{row.area || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ background: '#fffbeb', color: '#d97706', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>⏳ Pending</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvData.length > 100 && (
              <div style={{ textAlign: 'center', padding: '8px', fontSize: '12px', color: '#94a3b8' }}>Showing 100 of {csvData.length} records</div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={handleImport} disabled={importing}
                style={{ flex: 1, padding: '13px', background: importing ? '#e2e8f0' : primary, color: importing ? '#94a3b8' : 'white', border: 'none', borderRadius: '10px', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '15px' }}>
                {importing ? `⏳ Importing ${csvData.length} ONTs...` : `📥 Import ${csvData.length} ONTs`}
              </button>
              <button onClick={() => setStep(1)} style={{ padding: '13px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Step 3 - Results */}
        {step === 3 && results && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '56px', marginBottom: '12px' }}>🎉</div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '6px' }}>Import Complete!</h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
              ONTs will auto-populate with customer details once they connect to ACS
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              {[
                { label: 'Imported', value: results.imported, color: '#10b981', bg: '#ecfdf5', icon: '✅' },
                { label: 'Skipped', value: results.skipped, color: '#f59e0b', bg: '#fffbeb', icon: '⚠️' },
                { label: 'Failed', value: results.failed, color: '#ef4444', bg: '#fef2f2', icon: '❌' }
              ].map(s => (
                <div key={s.label} style={{ background: s.bg, borderRadius: '12px', padding: '16px' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{s.icon}</div>
                  <div style={{ fontSize: '26px', fontWeight: '800', color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Next steps */}
            <div style={{ textAlign: 'left', background: '#f0f9ff', borderRadius: '12px', padding: '16px', marginBottom: '20px', fontSize: '13px', color: '#0369a1' }}>
              <div style={{ fontWeight: '700', marginBottom: '8px' }}>📋 Next Steps:</div>
              {[
                '1️⃣ LCO engineer pushes ACS URL to OLT',
                '2️⃣ ONTs connect to ACS automatically',
                '3️⃣ PPPoE username auto-fills as Customer ID',
                '4️⃣ Vendor/Model auto-detected from TR-069',
                '5️⃣ Edit device to add customer name/phone'
              ].map((s, i) => <div key={i} style={{ marginBottom: '4px' }}>{s}</div>)}
            </div>

            {results.errors?.length > 0 && (
              <div style={{ textAlign: 'left', padding: '12px', background: '#fef2f2', borderRadius: '10px', marginBottom: '16px', maxHeight: '120px', overflowY: 'auto' }}>
                {results.errors.map((e, i) => <div key={i} style={{ fontSize: '12px', color: '#dc2626' }}>⚠️ {e}</div>)}
              </div>
            )}

            <button onClick={onClose}
              style={{ padding: '12px 32px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '15px' }}>
              ✅ Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkImport;
